import { FC } from 'react';
import { DozukiHeader, Link } from '~/ui-components';
import { useSiteInfo } from '~/pages/_shared';
import { useLoggedInUser } from '~/pages/_shared/useLoggedInUser';
import { useLocalization } from '~/framework';
import { useMarkNotificationAsRead } from './useMarkNotificationAsRead';
import { useMarkAllNotificationsAsRead } from './useMarkAllNotificationsAsRead';
import { useRecentNotifications } from './useRecentNotifications';
import { useRouter } from 'next/router';
import { Box, useToast } from '@chakra-ui/react';
import { UserPermissions as Permissions } from '~/monolith-api';
import { useActiveNavItem } from '../ActiveNavItemContext';

type PermissionMappings = {
   [key: string]: string;
};

type App = {
   appKey: string;
   route: string;
   name: string;
};

export const Header: FC = () => {
   const { data: siteInfo } = useSiteInfo();
   const { data: user } = useLoggedInUser();
   const router = useRouter();
   const activeNavItem = useActiveNavItem();
   const toast = useToast();

   const { flattenedData: notifications, total: totalUnreadNotifications } =
      useRecentNotifications();
   const markNotificationAsRead = useMarkNotificationAsRead();
   const markAllNotificationsAsRead = useMarkAllNotificationsAsRead();

   const { _js, translationSettings, handleTranslationSettingsChange } = useLocalization();

   function handleMarkAllNotificationsAsRead() {
      markAllNotificationsAsRead.mutate();
   }

   function handleMarkNotificationAsRead(notificationid: string) {
      markNotificationAsRead.mutate(notificationid);
   }

   const onRedirect = (url: string) => {
      router.push(url);
   };

   const permissionMappings: PermissionMappings = {
      'creator-pro': 'canIViewCreatorPro',
   };

   const filterApps = (apps: App[] | undefined, permissions: Permissions | undefined) => {
      if (!apps) {
         return [];
      }
      return apps.filter((app: App) => {
         const name = permissionMappings[app.appKey] as keyof Permissions;
         return permissions && name ? permissions[name] : true;
      });
   };

   const customHeaderHtml = siteInfo?.['appearance-header'];

   // Only hide logo and site name on header v2.
   // This logic can be removed when logo v2 is removed.
   const siteLogoUrl = siteInfo?.logo?.image.standard;
   const siteTitle = siteInfo?.title;

   return (
      <>
         {customHeaderHtml && (
            <Box
               aria-label="custom-header"
               dangerouslySetInnerHTML={{
                  __html: customHeaderHtml,
               }}
            />
         )}
         <DozukiHeader
            siteLogoUrl={siteLogoUrl}
            LinkComponent={Link}
            permissions={user?.permissions}
            siteTitle={siteTitle}
            user={user}
            siteConfiguration={siteInfo}
            notifications={notifications}
            totalUnreadNotifications={totalUnreadNotifications}
            onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
            onMarkNotificationAsRead={handleMarkNotificationAsRead}
            userTranslationSettings={translationSettings}
            onUserTranslationsSettingsChange={handleTranslationSettingsChange}
            onRedirect={onRedirect}
            _js={_js}
            toast={toast}
            activeItem={activeNavItem}
         />
      </>
   );
};
